import {
  AnnouncementSlugs,
  CardTypeCallToActions,
  CertificationDescription,
  SubscriptionType,
} from '@app/enums/_index';

import { Factory } from './_factory';
import { SubscriptionUIConfigurationItem } from '@app/models/_index';
import { environment } from '@environments/environment';

/**
 * Create Subscription UI Configuration Factory used by application
 * Creates instances of Subscription UI Configuration Items.
 */
export class SubscriptionUIConfigurationItemFactory
  implements Factory<SubscriptionUIConfigurationItem>
{
  uiConfigurations: SubscriptionUIConfigurationItem[] = [];
  certificationDescription: CertificationDescription;

  /**
   * Call and build static Subscription UI Configuration Items array.
   * Do not add or remove items from this array. Used as a read-only reference.
   */
  constructor() {
    this.build();
  }

  /**
   * Build static Subscription UI Configuration Items array
   */
  build(): void {
    this.uiConfigurations.push(
      new SubscriptionUIConfigurationItem(
        SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION,
        environment.beyond.engage.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.engage.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.engage.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.engage.featureFlags.showCertifications,
        environment.beyond.engage.featureFlags.showFlexibleLearning,
        environment.beyond.engage.featureFlags.showHeroBannerButton,
        environment.beyond.engage.featureFlags.showTestimonials,
        environment.beyond.engage.featureFlags.showContactUs,
        environment.beyond.engage.featureFlags.showCMSContent,
        environment.beyond.engage.featureFlags.showAnnouncements, // show announcement is true
        environment.beyond.engage.featureFlags.showAnnouncementsButton,
        environment.beyond.engage.featureFlags.showLearningPaths,
        environment.beyond.engage.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.engage.training_for_all_CX_roles,
        'Here are some recommended foundational courses to become a Professional.',
        AnnouncementSlugs.GENESYS_ENGAGE_SLUG,
        environment.beyond.engage.foundational_course_ids
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION,
        environment.beyond.genesys_cloud.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.genesys_cloud.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.genesys_cloud.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.genesys_cloud.featureFlags.showCertifications,
        environment.beyond.genesys_cloud.featureFlags.showFlexibleLearning,
        environment.beyond.genesys_cloud.featureFlags.showHeroBannerButton,
        environment.beyond.genesys_cloud.featureFlags.showTestimonials,
        environment.beyond.genesys_cloud.featureFlags.showContactUs,
        environment.beyond.genesys_cloud.featureFlags.showCMSContent,
        environment.beyond.genesys_cloud.featureFlags.showAnnouncements, // show announcement is false
        environment.beyond.genesys_cloud.featureFlags.showAnnouncementsButton,
        environment.beyond.genesys_cloud.featureFlags.showLearningPaths,
        environment.beyond.genesys_cloud.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.genesys_cloud.training_for_all_CX_roles,
        'Here are some recommended foundational courses to become a Professional.',
        '',
        environment.beyond.genesys_cloud.foundational_course_ids
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.PURECONNECT_SUBSCRIPTION,
        environment.beyond.pureconnect.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.pureconnect.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.pureconnect.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.pureconnect.featureFlags.showCertifications,
        environment.beyond.pureconnect.featureFlags.showFlexibleLearning,
        environment.beyond.pureconnect.featureFlags.showHeroBannerButton,
        environment.beyond.pureconnect.featureFlags.showTestimonials,
        environment.beyond.pureconnect.featureFlags.showContactUs,
        environment.beyond.pureconnect.featureFlags.showCMSContent,
        environment.beyond.pureconnect.featureFlags.showAnnouncements, // show announcement is false
        environment.beyond.pureconnect.featureFlags.showAnnouncementsButton,
        environment.beyond.pureconnect.featureFlags.showLearningPaths,
        environment.beyond.pureconnect.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.pureconnect.training_for_all_CX_roles,
        'Here are some recommended foundational courses to become a Professional.',
        '',
        environment.beyond.pureconnect.foundational_course_ids,
        '',
        false
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.POINTILLIST_SUBSCRIPTION,
        environment.beyond.pointillist.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.pointillist.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.pointillist.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.pointillist.featureFlags.showCertifications,
        environment.beyond.pointillist.featureFlags.showFlexibleLearning,
        environment.beyond.pointillist.featureFlags.showHeroBannerButton,
        environment.beyond.pointillist.featureFlags.showTestimonials,
        environment.beyond.pointillist.featureFlags.showContactUs,
        environment.beyond.pointillist.featureFlags.showCMSContent,
        environment.beyond.pointillist.featureFlags.showAnnouncements, // show announcement is true
        environment.beyond.pointillist.featureFlags.showAnnouncementsButton,
        environment.beyond.pointillist.featureFlags.showLearningPaths,
        environment.beyond.pointillist.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.pointillist.training_for_all_CX_roles,
        'Here are three free Pointillist by Genesys courses to get you started.',
        AnnouncementSlugs.POINTILLIST_SLUG,
        environment.beyond.pointillist.foundational_course_ids,
        'font-size-large'
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.AGENT_PLATFORM_SUBSCRIPTION,
        environment.beyond.agent_platform.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.agent_platform.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.agent_platform.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.agent_platform.featureFlags.showCertifications,
        environment.beyond.agent_platform.featureFlags.showFlexibleLearning,
        environment.beyond.agent_platform.featureFlags.showHeroBannerButton,
        environment.beyond.agent_platform.featureFlags.showTestimonials,
        environment.beyond.agent_platform.featureFlags.showContactUs,
        environment.beyond.agent_platform.featureFlags.showCMSContent,
        environment.beyond.agent_platform.featureFlags.showAnnouncements, // show announcement is false
        environment.beyond.agent_platform.featureFlags.showAnnouncementsButton,
        environment.beyond.agent_platform.featureFlags.showLearningPaths,
        environment.beyond.agent_platform.featureFlags.showModal,
        CardTypeCallToActions.SIGN_IN,
        environment.docebo.agent_platform_certification_url,
        CertificationDescription.AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.agent_platform.training_for_all_CX_roles
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION,
        environment.beyond.beyondcx.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.beyondcx.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.beyondcx.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.beyondcx.featureFlags.showCertifications,
        environment.beyond.beyondcx.featureFlags.showFlexibleLearning,
        false,
        environment.beyond.beyondcx.featureFlags.showTestimonials,
        environment.beyond.beyondcx.featureFlags.showContactUs,
        environment.beyond.beyondcx.featureFlags.showCMSContent,
        environment.beyond.beyondcx.featureFlags.showAnnouncements,
        environment.beyond.beyondcx.featureFlags.showAnnouncementsButton,
        environment.beyond.beyondcx.featureFlags.showLearningPaths,
        environment.beyond.beyondcx.featureFlags.showModal,
        '',
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.beyondcx.training_for_all_CX_roles
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showCertifications,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showFlexibleLearning,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showHeroBannerButton,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showTestimonials,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showContactUs,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showCMSContent,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showAnnouncements, // show announcement is false
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showAnnouncementsButton,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showLearningPaths,
        environment.beyond.genesys_cloud_partner_premier.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.genesys_cloud_partner_premier.training_for_all_CX_roles,
        'Here are some recommended foundational courses to become a Professional.',
        '',
        environment.beyond.genesys_cloud_partner_premier.foundational_course_ids
      ),
      new SubscriptionUIConfigurationItem(
        SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION,
        environment.beyond.genesys_cloud_trial.featureFlags.showRecommendedFoundationalCourses,
        environment.beyond.genesys_cloud_trial.featureFlags.showRecommendedFoundationalHeading,
        environment.beyond.genesys_cloud_trial.featureFlags.showTrainingForAllCXRoles,
        environment.beyond.genesys_cloud_trial.featureFlags.showCertifications,
        environment.beyond.genesys_cloud_trial.featureFlags.showFlexibleLearning,
        environment.beyond.genesys_cloud_trial.featureFlags.showHeroBannerButton,
        environment.beyond.genesys_cloud_trial.featureFlags.showTestimonials,
        environment.beyond.genesys_cloud_trial.featureFlags.showContactUs,
        environment.beyond.genesys_cloud_trial.featureFlags.showCMSContent,
        environment.beyond.genesys_cloud_trial.featureFlags.showAnnouncements, // show announcement is false
        environment.beyond.genesys_cloud_trial.featureFlags.showAnnouncementsButton,
        environment.beyond.genesys_cloud_trial.featureFlags.showLearningPaths,
        environment.beyond.genesys_cloud_trial.featureFlags.showModal,
        environment.beyond.featureFlags.showContactSales
          ? CardTypeCallToActions.CONTACT_SALES
          : CardTypeCallToActions.REQUEST_A_QUOTE,
        'certification/home',
        CertificationDescription.NON_AGENT_PLATFORM_SUBSCRIPTION_CERTIFICATION_DESCRIPTION,
        environment.beyond.genesys_cloud_trial.training_for_all_CX_roles,
        'Here are some recommended foundational courses to become a Professional.',
        '',
        environment.beyond.genesys_cloud_trial.foundational_course_ids
      )
    );
  }
  /**
   * Return immutable or copy of Subscription UI Configuration Items collection
   * @returns - a slice or copy of the static collection
   */
  getCollection(): SubscriptionUIConfigurationItem[] {
    if (this.uiConfigurations && this.uiConfigurations.length === 0) {
      this.build();
    }
    return this.uiConfigurations.slice();
  }

  /**
   * Return the Subscription UI Configuration Item by subscription type
   * @param subscriptionType - subscription type slug
   * @returns - a the first Subscription UI Configuration that matches the subscription type
   */
  getSubscriptionUIConfigurationItem(subscriptionType: string): SubscriptionUIConfigurationItem {
    return this.getCollection().find(
      (item: SubscriptionUIConfigurationItem) => item.subscriptionType === subscriptionType
    );
  }
}
